import React, { useState, useRef, useEffect } from 'react';
import { useTripContext, TRIP_TYPES, CABIN_CLASSES } from '../../contexts/TripContext';

/**
 * TripFilters - Trip type, passengers, cabin class, stops, bags filters
 */
const TripFilters = () => {
  const {
    tripType,
    setTripType,
    passengers,
    setPassengers,
    cabinClass,
    setCabinClass,
    checkedBags,
    setCheckedBags,
    getTotalPassengers,
  } = useTripContext();

  const [activePopup, setActivePopup] = useState(null);
  const filterRef = useRef(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const togglePopup = (popupName) => {
    setActivePopup(activePopup === popupName ? null : popupName);
  };

  return (
    <div className="trip-filters" ref={filterRef}>
      {/* Trip Type Filter */}
      <div className="filter-group" onClick={() => togglePopup('tripType')}>
        <i className="fas fa-exchange-alt"></i>
        <span>{tripType === TRIP_TYPES.ROUND_TRIP ? 'Round trip' : 'One way'}</span>
        <i className="fas fa-caret-down"></i>

        {activePopup === 'tripType' && (
          <div className="filter-popup active" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <span>Ticket type</span>
              <i className="fas fa-times" onClick={() => setActivePopup(null)}></i>
            </div>
            <div
              className={`popup-option ${tripType === TRIP_TYPES.ROUND_TRIP ? 'selected' : ''}`}
              onClick={() => {
                setTripType(TRIP_TYPES.ROUND_TRIP);
                setActivePopup(null);
              }}
            >
              <i className="far fa-dot-circle selected-icon"></i>
              <i className="far fa-circle unselected-icon"></i>
              <span>Round trip</span>
            </div>
            <div
              className={`popup-option ${tripType === TRIP_TYPES.ONE_WAY ? 'selected' : ''}`}
              onClick={() => {
                setTripType(TRIP_TYPES.ONE_WAY);
                setActivePopup(null);
              }}
            >
              <i className="far fa-dot-circle selected-icon"></i>
              <i className="far fa-circle unselected-icon"></i>
              <span>One way</span>
            </div>
          </div>
        )}
      </div>

      {/* Passengers Filter */}
      <div className="filter-group" onClick={() => togglePopup('passengers')}>
        <i className="fas fa-user"></i>
        <span>{getTotalPassengers()}</span>
        <i className="fas fa-caret-down"></i>

        {activePopup === 'passengers' && (
          <div className="filter-popup active" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <span>Passengers</span>
              <i className="fas fa-times" onClick={() => setActivePopup(null)}></i>
            </div>
            <div className="popup-option">
              <span>Adults</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  style={{ padding: '5px 10px', cursor: 'pointer' }}
                  onClick={() => setPassengers({ ...passengers, adults: Math.max(1, passengers.adults - 1) })}
                >-</button>
                <span>{passengers.adults}</span>
                <button
                  style={{ padding: '5px 10px', cursor: 'pointer' }}
                  onClick={() => setPassengers({ ...passengers, adults: passengers.adults + 1 })}
                >+</button>
              </div>
            </div>
            <div className="popup-option">
              <span>Children</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  style={{ padding: '5px 10px', cursor: 'pointer' }}
                  onClick={() => setPassengers({ ...passengers, children: Math.max(0, passengers.children - 1) })}
                >-</button>
                <span>{passengers.children}</span>
                <button
                  style={{ padding: '5px 10px', cursor: 'pointer' }}
                  onClick={() => setPassengers({ ...passengers, children: passengers.children + 1 })}
                >+</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cabin Class Filter */}
      <div className="filter-group" onClick={() => togglePopup('cabin')}>
        <span>{cabinClass}</span>
        <i className="fas fa-caret-down"></i>

        {activePopup === 'cabin' && (
          <div className="filter-popup active" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <span>Cabin class</span>
              <i className="fas fa-times" onClick={() => setActivePopup(null)}></i>
            </div>
            {Object.values(CABIN_CLASSES).map((cabin) => (
              <div
                key={cabin}
                className={`popup-option ${cabinClass === cabin ? 'selected' : ''}`}
                onClick={() => {
                  setCabinClass(cabin);
                  setActivePopup(null);
                }}
              >
                <i className="far fa-dot-circle selected-icon"></i>
                <i className="far fa-circle unselected-icon"></i>
                <span>{cabin}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stops Filter (placeholder) */}
      <div className="filter-group">
        <span>Stops</span>
        <i className="fas fa-caret-down"></i>
      </div>

      {/* Bags Filter */}
      <div className="filter-group" onClick={() => togglePopup('bags')}>
        <i className="fas fa-suitcase"></i>
        <span>{checkedBags}</span>
        <i className="fas fa-caret-down"></i>

        {activePopup === 'bags' && (
          <div className="filter-popup active" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <span>Checked bags</span>
              <i className="fas fa-times" onClick={() => setActivePopup(null)}></i>
            </div>
            <div className="popup-option">
              <span>Checked bags per person</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  style={{ padding: '5px 10px', cursor: 'pointer' }}
                  onClick={() => setCheckedBags(Math.max(0, checkedBags - 1))}
                >-</button>
                <span>{checkedBags}</span>
                <button
                  style={{ padding: '5px 10px', cursor: 'pointer' }}
                  onClick={() => setCheckedBags(checkedBags + 1)}
                >+</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripFilters;
